import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, ScreenHeader, Segmented, SectionTitle, ProgressBar } from "../ui";
import { Icon } from "../Icon";
import { ProgressRing } from "../ProgressRing";
import { useI18n } from "../i18n";
import { useAppState } from "../state";
import { AREAS, LESSONS, t } from "../content";

function Stat({ icon, color, value, label }) {
  return (
    <Card style={{ flex: 1, alignItems: "center", paddingTop: 18, paddingBottom: 18, gap: 4 }}>
      <Icon name={icon} size={22} color={color} weight="fill" />
      <Text style={{ fontWeight: fonts.bold, fontSize: 20, color: colors.onSurface }}>{value}</Text>
      <Text style={{ fontWeight: fonts.regular, fontSize: 11, color: colors.muted, textAlign: "center" }}>{label}</Text>
    </Card>
  );
}

export default function Progress() {
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const st = useAppState();
  const [tab, setTab] = useState("overview");
  const readiness = st.readiness();
  const lessonsDone = Object.values(st.state.lessons).filter((l) => l.completed).length;
  const acc = st.state.answered ? Math.round((st.state.correct / st.state.answered) * 100) : 0;
  const daysToExam = st.daysToExam();

  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={tr("yourProgress")} onBack={() => navigate(-1)} />
      <View style={{ padding: "12px 20px" }}>
        <Segmented value={tab} onChange={setTab} items={[{ key: "overview", label: tr("overview") }, { key: "topics", label: tr("topics") }]} />
      </View>
      <View style={{ padding: "4px 20px 32px" }}>
        {tab === "overview" ? (
          <>
            <Card style={{ alignItems: "center", paddingTop: 24, paddingBottom: 24, marginBottom: 14 }}>
              <ProgressRing progress={readiness} size={150} label={`${readiness}%`} sublabel={tr("examReadiness")} />
              <View style={{ backgroundColor: readiness >= 75 ? colors.successSurface : readiness >= 50 ? colors.warningSurface : colors.errorSurface, borderRadius: 999, padding: "6px 16px", marginTop: 12 }}>
                <Text style={{ fontWeight: fonts.bold, fontSize: 13, color: readiness >= 75 ? colors.onSuccessSurface : readiness >= 50 ? colors.onWarningSurface : colors.onErrorSurface }}>
                  {readiness >= 75 ? tr("strong") : readiness >= 50 ? tr("improve") : tr("critical")}
                </Text>
              </View>
            </Card>
            <View row style={{ gap: 12, marginBottom: 12 }}>
              <Stat icon="cap" color={colors.brandPrimary} value={st.state.answered} label={tr("questionsAnswered")} />
              <Stat icon="target" color={colors.success} value={`${acc}%`} label={tr("accuracy")} />
            </View>
            <View row style={{ gap: 12, marginBottom: 12 }}>
              <Stat icon="book-open" color={colors.info} value={`${lessonsDone}/${LESSONS.length}`} label={tr("lessonsDone")} />
              <Stat icon="flame" color={colors.warning} value={st.state.streak.count} label={tr("streak")} />
            </View>
            <View row style={{ gap: 12 }}>
              <Stat icon="cards" color={colors.info} value={st.masteredCards()} label={tr("cardsMastered")} />
              <Stat icon="calendar" color={colors.brandPrimary} value={daysToExam ?? "—"} label={tr("daysToExam")} />
            </View>
          </>
        ) : (
          <>
            <SectionTitle>{tr("strengths")}</SectionTitle>
            {AREAS.map((area) => {
              const pct = st.areaAccuracy(area.id);
              const color = pct === null ? colors.border : pct >= 75 ? colors.success : pct >= 50 ? colors.warning : colors.error;
              return (
                <Card key={area.id} style={{ marginBottom: 10 }}>
                  <View row style={{ justifyContent: "space-between", marginBottom: 8 }}>
                    <Text numberOfLines={1} style={{ flex: 1, fontWeight: fonts.semibold, fontSize: 14, color: colors.onSurface }}>{area.num} · {t(area.title, lang)}</Text>
                    <Text style={{ fontWeight: fonts.bold, fontSize: 14, color: pct === null ? colors.muted : color }}>{pct === null ? "—" : `${pct}%`}</Text>
                  </View>
                  <ProgressBar progress={pct ?? 0} color={pct === null ? colors.border : color} />
                </Card>
              );
            })}
          </>
        )}
      </View>
    </View>
  );
}
